// Employee Activity — shared inner panel (stats + search + table + refresh).
//
// Rendered by BOTH the modal (EmployeeActivityModal) and the standalone page
// (EmployeeActivity) so the UI + data logic live in exactly one place.
//
// Fetching is gated by the `active` prop: the panel only loads when it becomes
// active (modal opened / page mounted) so a closed modal never polls the backend.
//
// Rate limiting (defense in depth, per the approved design):
//   - Client: Refresh disables for REFRESH_COOLDOWN_MS after each click.
//   - Server: the endpoint is per-user rate-limited and returns 429 → shown as a toast.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, Tag, Input, Tooltip, Empty } from 'antd';
import { RefreshCw, Search, Wifi, WifiOff, Activity as ActivityIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { fmtTime, tzLabel } from '../stealth-clock/timeFmt';
import { getEmployeeActivity, getAppActivity } from './api';

const REFRESH_COOLDOWN_MS = 10000; // client-side backstop; server also rate-limits

// Two independent live-status sources for the same people. They can legitimately
// DISAGREE (agent running but timer stopped, or vice versa) — that difference is
// the point of the toggle, not a bug.
const MODES = {
  agent: {
    key: 'agent',
    label: 'Desktop Agent',
    fetch: getEmployeeActivity,
    hasActivity: true,
    hasStarted: true,
    hint: 'Online = the desktop agent reported in the last ~2.5 minutes.',
  },
  app: {
    key: 'app',
    label: 'Desktop Application',
    fetch: getAppActivity,
    hasActivity: false,
    hasStarted: true,
    hint: 'Online = the tracking timer pinged in the last 2 minutes. Users on older desktop builds may show Offline while tracking.',
  },
};

function StatusTag({ status }) {
  if (status === 'online') {
    return (
      <Tag color="green" style={{ borderRadius: 12, paddingInline: 10 }}>
        <span className="inline-flex items-center gap-1">
          <Wifi size={12} /> Online
        </span>
      </Tag>
    );
  }
  return (
    <Tag color="default" style={{ borderRadius: 12, paddingInline: 10 }}>
      <span className="inline-flex items-center gap-1">
        <WifiOff size={12} /> Offline
      </span>
    </Tag>
  );
}

function ActivityTag({ activity }) {
  if (activity === 'active') {
    return (
      <Tooltip title="Actively working — a fresh report was received recently">
        <Tag color="blue" style={{ borderRadius: 12, paddingInline: 10 }}>
          <span className="inline-flex items-center gap-1">
            <ActivityIcon size={12} /> Active
          </span>
        </Tag>
      </Tooltip>
    );
  }
  if (activity === 'idle') {
    return (
      <Tooltip title="Online but no recent activity">
        <Tag color="orange" style={{ borderRadius: 12, paddingInline: 10 }}>Idle</Tag>
      </Tooltip>
    );
  }
  return <span className="text-gray-400">—</span>;
}

/**
 * @param {boolean} active  - when true the panel is visible and should load/refresh.
 *                            When false (e.g. modal closed) no fetch happens.
 * @param {boolean} inModal - when true the table gets a fixed-height internal scroll
 *                            region with a sticky header (so a long list scrolls inside
 *                            the modal instead of growing the modal past the viewport).
 */
export default function EmployeeActivityPanel({ active = true, inModal = false }) {
  // Aliased: `mode` below is the Agent/Application source toggle.
  const { mode: themeMode } = useTheme() || {};
  const isDark = themeMode === 'dark';

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0, active: 0, idle: 0 });
  const [orgTz, setOrgTz] = useState(''); // organization.settings.timezone from API meta
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [q, setQ] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const [mode, setMode] = useState('agent'); // 'agent' (default) | 'app'
  const cooldownTimer = useRef(null);
  const searchDebounce = useRef(null);
  // Guards against a slow response for a mode the user has since switched away
  // from overwriting the newer mode's rows.
  const reqSeq = useRef(0);

  const load = useCallback(async (searchStr = '', modeKey = 'agent') => {
    const cfg = MODES[modeKey] || MODES.agent;
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const data = await cfg.fetch({ q: searchStr || undefined, limit: 500 });
      if (seq !== reqSeq.current) return; // stale response — a newer request won
      if (data && data.ok) {
        setRows(data.employees || []);
        setStats(data.stats || { total: 0, online: 0, offline: 0, active: 0, idle: 0 });
        if (data.meta && data.meta.timezone) setOrgTz(data.meta.timezone);
      } else {
        toast.error('Could not load employee activity');
      }
    } catch (err) {
      if (seq !== reqSeq.current) return;
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(err?.response?.data?.message || 'Too many refreshes — please wait a moment.');
      } else if (status === 403) {
        toast.error('Employee Activity is not enabled for your organization.');
      } else {
        toast.error('Failed to load employee activity');
      }
    } finally {
      if (seq === reqSeq.current) {
        setLoading(false);
        setLoadedOnce(true);
      }
    }
  }, []);

  // Load when the panel becomes active (modal opened / page mounted). Only the first
  // activation triggers a fetch; re-renders while already active don't re-fetch.
  useEffect(() => {
    if (active && !loadedOnce) load('', mode);
  }, [active, loadedOnce, load, mode]);

  // Switching source re-fetches immediately, preserving the current search text.
  const onModeChange = (nextMode) => {
    if (nextMode === mode || loading) return;
    setMode(nextMode);
    setRows([]);                       // don't show the old source's rows under the new label
    setStats({ total: 0, online: 0, offline: 0, active: 0, idle: 0 });
    load(q.trim(), nextMode);
  };

  // Cleanup timers on unmount.
  useEffect(() => () => {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
  }, []);

  // Debounced search.
  const onSearchChange = (e) => {
    const val = e.target.value;
    setQ(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => load(val.trim(), mode), 350);
  };

  const onRefresh = () => {
    if (cooldown || loading) return;
    load(q.trim(), mode);
    // Start client cooldown regardless — prevents hammering even on fast responses.
    setCooldown(true);
    cooldownTimer.current = setTimeout(() => setCooldown(false), REFRESH_COOLDOWN_MS);
  };

  const modeCfg = MODES[mode] || MODES.agent;

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {r.name || r.email || 'Team member'}
          </div>
          {(r.designation || r.email) && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {[r.designation, r.email].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, r) => <StatusTag status={r.status} />,
    },
    // The Application source has no trustworthy live active/idle signal, so the
    // column is dropped entirely there rather than rendering a column of dashes.
    ...(modeCfg.hasActivity
      ? [{
          title: 'Activity',
          key: 'activity',
          width: 140,
          render: (_, r) => <ActivityTag activity={r.activity} />,
        }]
      : []),
    // Both modes: first signal today (agent report / app session), org-TZ day + display.
    ...(modeCfg.hasStarted
      ? [{
          title: (
            <Tooltip
              title={
                mode === 'agent'
                  ? `First agent report today · ${tzLabel(orgTz) || 'org timezone'}`
                  : `First tracking session today · ${tzLabel(orgTz) || 'org timezone'}`
              }
            >
              <span>Started</span>
            </Tooltip>
          ),
          key: 'tracking_started_at',
          width: 120,
          render: (_, r) => {
            const label = fmtTime(r.tracking_started_at, orgTz);
            if (!label) return <span className="text-gray-400">—</span>;
            return (
              <Tooltip
                title={
                  mode === 'agent'
                    ? `Today’s first report (${tzLabel(orgTz)})`
                    : `Today’s first session (${tzLabel(orgTz)})`
                }
              >
                <span className="text-sm text-gray-700 dark:text-gray-200 tabular-nums">
                  {label}
                </span>
              </Tooltip>
            );
          },
        }]
      : []),
  ];

  const chip = (label, value, cls) => (
    <div className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${cls}`}>
      <span className="font-semibold">{value}</span>
      <span className="opacity-80">{label}</span>
    </div>
  );

  return (
    <div className={inModal ? 'flex flex-col min-h-0 flex-1' : ''}>
      {/* Source toggle — Agent (default) / Application. Two different live signals
          for the same people; they can legitimately disagree. */}
      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {Object.values(MODES).map((m) => {
            const isSel = m.key === mode;
            return (
              <button
                key={m.key}
                onClick={() => onModeChange(m.key)}
                disabled={loading && !isSel}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isSel
                    ? (isDark ? 'bg-gray-900 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                    : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                } ${loading && !isSel ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <Tooltip title={modeCfg.hint}>
          <span className="text-xs text-gray-500 dark:text-gray-400 cursor-help">
            What does Online mean?
          </span>
        </Tooltip>
      </div>

      {/* Toolbar: search + refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
        <div className="max-w-sm w-full sm:w-64">
          <Input
            allowClear
            value={q}
            onChange={onSearchChange}
            placeholder="Search employee or email"
            prefix={<Search size={14} className="text-gray-400" />}
          />
        </div>
        <button
          onClick={onRefresh}
          disabled={cooldown || loading}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            cooldown || loading
              ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 !text-white hover:bg-blue-700'
          }`}
          title={cooldown ? 'Please wait a few seconds between refreshes' : 'Refresh'}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          {cooldown ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Stat chips */}
      <div className="flex flex-wrap gap-2 mb-4 flex-shrink-0">
        {chip('Total', stats.total, isDark ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700')}
        {chip('Online', stats.online, 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300')}
        {/* Active/Idle only exist for the Agent source — hidden (not zeroed) for
            the Application source so an empty count can't read as "nobody active". */}
        {modeCfg.hasActivity && chip('Active', stats.active, 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300')}
        {modeCfg.hasActivity && chip('Idle', stats.idle, 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300')}
        {chip('Offline', stats.offline, isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500')}
      </div>

      {/* Table — in the modal it fills the remaining space and scrolls its BODY only
          (sticky header, no pager) so a long team list stays inside the modal. */}
      <div className={inModal ? 'flex-1 min-h-0 overflow-hidden' : ''}>
        <Table
          rowKey={(r) => r.user_id}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={inModal ? false : { pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={inModal ? { y: 'calc(100vh - 420px)' } : undefined}
          sticky={inModal}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={loading ? 'Loading…' : 'No employees to show yet'}
              />
            ),
          }}
          size="middle"
        />
      </div>
    </div>
  );
}
